(function () {
  if (window.awSmartPopupLoaded) return;
  window.awSmartPopupLoaded = true;

  const DAY = 86400000;
  const DURATION = { day: DAY, week: 7 * DAY, month: 30 * DAY };
  const MOBILE_QUERY = '(max-width: 749px)';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function safeStorage(type) {
    try {
      const store = window[type];
      const probe = '__aw_probe__';
      store.setItem(probe, '1');
      store.removeItem(probe);
      return store;
    } catch (error) {
      return null;
    }
  }

  const localStore = safeStorage('localStorage');
  const sessionStore = safeStorage('sessionStorage');

  function whenIdle(callback) {
    const run = () =>
      'requestIdleCallback' in window
        ? window.requestIdleCallback(callback, { timeout: 3000 })
        : window.setTimeout(callback, 1200);

    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  class AwSmartPopup {
    constructor(dialog) {
      this.dialog = dialog;
      this.data = dialog.dataset;
      this.key = 'aw-popup-' + this.data.sectionId + (this.data.giftMode === 'true' ? '-mirror-pilot' : '');
      this.isDesignMode = this.data.designMode === 'true';
      this.supportsModal = typeof dialog.showModal === 'function';
      this.opened = false;
      this.scrollY = 0;
      this.backdrop = null;
      this.timer = null;

      this.onClick = this.onClick.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onNativeClose = this.onNativeClose.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onExit = this.onExit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      dialog.addEventListener('click', this.onClick);
      dialog.addEventListener('cancel', this.onCancel);
      dialog.addEventListener('close', this.onNativeClose);

      this.schedule();
    }

    schedule() {
      if (this.dialog.querySelector('[data-aw-popup-posted]')) {
        this.open(true);
        return;
      }

      if (!this.shouldShow()) return;

      this.prefetchMedia();

      if (this.isDesignMode) {
        this.timer = window.setTimeout(() => this.open(), 300);
        return;
      }

      const trigger = this.data.trigger;

      if (trigger === 'scroll') {
        document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
        this.onScroll();
      } else if (trigger === 'exit' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        document.addEventListener('mouseout', this.onExit);
      } else {
        this.timer = window.setTimeout(() => this.open(), Number(this.data.delay || 0) * 1000);
      }
    }

    shouldShow() {
      if (this.isDesignMode) return true;
      if (this.data.enabled !== 'true') return false;
      if (this.data.giftMode === 'true') {
        if (this.data.knownCustomer === 'true') return false;
        if (localStore && localStore.getItem('tini-mirror-gift-requested')) return false;
      }
      if (this.data.showMobile !== 'true' && window.matchMedia(MOBILE_QUERY).matches) return false;

      const frequency = this.data.frequency;
      if (frequency === 'always') return true;
      if (frequency === 'session') return !(sessionStore && sessionStore.getItem(this.key));
      if (!localStore) return true;

      const closedAt = Number(localStore.getItem(this.key) || 0);
      const customMs = Math.max(1, Number(this.data.repeatDays || 1)) * DAY;
      const duration = frequency === 'custom' ? customMs : DURATION[frequency] || DURATION.week;
      return Date.now() - closedAt > duration;
    }

    getScrollRoot() {
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY)) return wrapper;
      return document.scrollingElement || document.documentElement;
    }

    onScroll() {
      const root = this.getScrollRoot();
      const available = root.scrollHeight - root.clientHeight;
      if (available <= 0) return;
      if ((root.scrollTop / available) * 100 >= Number(this.data.scroll || 40)) this.open();
    }

    onExit(event) {
      if (event.clientY <= 0 && !event.relatedTarget) this.open();
    }

    clearTriggers() {
      window.clearTimeout(this.timer);
      this.timer = null;
      document.removeEventListener('scroll', this.onScroll, true);
      document.removeEventListener('mouseout', this.onExit);
    }

    prefetchMedia() {
      const image = this.dialog.querySelector('img[loading="lazy"]');
      if (!image) return;
      whenIdle(() => {
        image.loading = 'eager';
      });
    }

    open(force) {
      if (this.opened) return;
      if (!force && !this.shouldShow()) return;

      this.clearTriggers();
      this.opened = true;
      // A gift invitation appears once per tab session, including page navigation.
      if (this.data.giftMode === 'true' && this.data.frequency === 'session') this.remember();
      this.previouslyFocused = document.activeElement;
      this.lockScroll();

      const image = this.dialog.querySelector('img[loading="lazy"]');
      if (image) image.loading = 'eager';

      if (this.supportsModal) {
        this.dialog.showModal();
      } else {
        this.openFallback();
      }

      const video = this.dialog.querySelector('video');
      if (video) {
        video.muted = true;
        video.playsInline = true;
        const playPromise = video.play();
        if (playPromise && playPromise.catch) playPromise.catch(() => {});
      }

      window.requestAnimationFrame(() => {
        const posted = this.dialog.querySelector('[data-aw-popup-posted]');
        (posted || this.dialog).focus({ preventScroll: true });
      });
    }

    openFallback() {
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'aw-smart-popup__backdrop';
      this.backdrop.style.background = 'rgb(0 0 0 / ' + (this.data.overlay || 0.45) + ')';
      this.backdrop.addEventListener('click', () => {
        if (this.data.closeOverlay === 'true') this.close();
      });
      this.dialog.parentNode.insertBefore(this.backdrop, this.dialog);
      this.dialog.setAttribute('open', '');
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      if (!this.opened) return;
      if (this.supportsModal) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.onNativeClose();
      }
    }

    onNativeClose() {
      if (!this.opened) return;
      this.opened = false;

      this.unlockScroll();

      const video = this.dialog.querySelector('video');
      if (video && !video.paused) video.pause();

      if (this.backdrop) {
        this.backdrop.remove();
        this.backdrop = null;
      }

      document.removeEventListener('keydown', this.onKeydown);
      this.remember();

      if (this.previouslyFocused && this.previouslyFocused.focus) {
        this.previouslyFocused.focus({ preventScroll: true });
      }
    }

    onCancel(event) {
      if (this.data.closeEscape !== 'true') event.preventDefault();
    }

    onClick(event) {
      if (event.target.closest('[data-aw-popup-close]')) {
        this.close();
        return;
      }

      const copyButton = event.target.closest('[data-aw-copy-code]');
      if (copyButton) {
        this.copyCode(copyButton);
        return;
      }

      if (event.target === this.dialog && this.data.closeOverlay === 'true') this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.data.closeEscape === 'true') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (!items.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      this.scrollRoot = this.getScrollRoot();
      this.wrapperScroll = this.scrollRoot.classList.contains('page-wrapper');
      this.scrollY = this.scrollRoot.scrollTop;
      const scrollbar = this.wrapperScroll
        ? this.scrollRoot.offsetWidth - this.scrollRoot.clientWidth
        : window.innerWidth - document.documentElement.clientWidth;
      if (this.wrapperScroll) {
        this.previousOverflow = this.scrollRoot.style.overflowY;
        this.scrollRoot.style.overflowY = 'hidden';
      }
      document.documentElement.classList.add('aw-popup-open');
      document.body.style.top = this.wrapperScroll ? '0px' : '-' + this.scrollY + 'px';
      if (scrollbar > 0) document.body.style.paddingRight = scrollbar + 'px';
    }

    unlockScroll() {
      document.documentElement.classList.remove('aw-popup-open');
      document.body.style.top = '';
      document.body.style.paddingRight = '';
      if (this.wrapperScroll) {
        this.scrollRoot.style.overflowY = this.previousOverflow;
        this.scrollRoot.scrollTo({ top: this.scrollY, left: 0, behavior: 'instant' });
      } else {
        window.scrollTo({ top: this.scrollY, left: 0, behavior: 'instant' });
      }
    }

    remember() {
      const frequency = this.data.frequency;
      if (frequency === 'always' || this.isDesignMode) return;
      try {
        if (frequency === 'session') {
          if (sessionStore) sessionStore.setItem(this.key, '1');
        } else if (localStore) {
          localStore.setItem(this.key, String(Date.now()));
        }
      } catch (error) {
        return;
      }
    }

    async copyCode(button) {
      const code = button.dataset.awCopyCode || '';
      let copied = false;

      try {
        await navigator.clipboard.writeText(code);
        copied = true;
      } catch (error) {
        const helper = document.createElement('textarea');
        helper.value = code;
        helper.setAttribute('readonly', '');
        helper.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        this.dialog.appendChild(helper);
        helper.select();
        try {
          copied = document.execCommand('copy');
        } catch (fallbackError) {
          copied = false;
        }
        helper.remove();
      }

      const label = button.querySelector('[data-aw-copy-text]');
      if (!label || !copied) return;

      label.textContent = button.dataset.copiedLabel || 'Copied!';
      window.setTimeout(() => {
        label.textContent = button.dataset.copyLabel || 'Copy code';
      }, 1800);
    }

    destroy() {
      this.clearTriggers();
      document.removeEventListener('keydown', this.onKeydown);
      if (this.opened) {
        this.unlockScroll();
        this.opened = false;
      }
      if (this.backdrop) {
        this.backdrop.remove();
        this.backdrop = null;
      }
    }
  }

  function initialize(root) {
    (root || document).querySelectorAll('[data-aw-smart-popup]').forEach((dialog) => {
      if (dialog.awSmartPopup) dialog.awSmartPopup.destroy();
      dialog.awSmartPopup = new AwSmartPopup(dialog);
    });
  }

  initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const dialog = event.target.querySelector('[data-aw-smart-popup]');
    if (dialog && dialog.awSmartPopup) dialog.awSmartPopup.destroy();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const dialog = event.target.closest('[data-aw-smart-popup]');
    if (dialog && dialog.awSmartPopup) dialog.awSmartPopup.open(true);
  });
})();